import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

// * Icons
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// * Queries
import { useLazyGetBuildingByIdQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { useLazyGetDataQuery } from '@/api/recalc/endpoints/dataApiSlice';
import { useLazyGetGatewayByIdQuery } from '@/api/recalc/endpoints/gatewayApiSlice';
import { useLazyGetSubscriptionByIdQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { useLazyGetSensorsByIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';
import { useLazyGetUnitByIdQuery } from '@/api/recalc/endpoints/unitsApiSlice';

import UnitAlarmChart from '../highcharts/UnitAlarmChart';

const formatAndEncodeTimestampForAPI = (date) => {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
};

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const SEVERITY_BUTTON_CONFIG = {
  Critical: {
    label: 'Critical',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Critical,
  },
  Major: {
    label: 'Major',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Major,
  },
  Medium: {
    label: 'Medium',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Medium,
  },
  Minor: {
    label: 'Minor',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Minor,
  },
  Information: {
    label: 'Information',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Information,
  },
  Trivial: {
    label: 'Trivial',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Trivial,
  },
};

const getAlertSeverity = (severity) => {
  switch (severity) {
    case 'Critical':
      return 'error';
    case 'Major':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Minor':
      return 'warning';
    case 'Trivial':
      return 'success';
    case 'Information':
      return 'info';
    default:
      return 'default';
  }
};

function formatDanishDate(timestamp) {
  if (!timestamp) return '';
  return DateTime.fromISO(timestamp, { zone: 'utc' })
    .setLocale('da')
    .toFormat('dd. MMM yyyy HH:mm:ss');
}

const ViewAlarmDialog = ({ open, onClose, alarm }) => {
  const [associationContent, setAssociationContent] = useState(null);

  const handleClose = () => {
    onClose();
    setAssociationContent(null);
  };

  const [getUnitById] = useLazyGetUnitByIdQuery();
  const [getSensorsById] = useLazyGetSensorsByIdQuery();
  const [getSubscriptionById] = useLazyGetSubscriptionByIdQuery();
  const [getBuildingById] = useLazyGetBuildingByIdQuery();
  const [getGatewayById] = useLazyGetGatewayByIdQuery();
  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  useEffect(() => {
    if (!alarm?.Associations) return;

    const { Units, Sensors, Buildings, JobSubscriptions, Gateways } =
      alarm.Associations;

    const fetchAssociations = async () => {
      try {
        if (Units.length > 0) {
          const units = await Promise.all(
            Units.map((id) => getUnitById(id).unwrap()),
          );

          if (alarm?.DataFrom && alarm?.DataTo) {
            const from = DateTime.fromISO(alarm.DataFrom)
              .minus({ hours: 12 })
              .toMillis();
            const to = DateTime.fromISO(alarm.DataTo)
              .plus({ hours: 12 })
              .toMillis();

            const seriesPerUnit = {};

            await Promise.all(
              units.map(async (unit) => {
                const sensorSeries = await Promise.all(
                  (unit.UnitSensors ?? []).map(async (unitSensor) => {
                    if (!unitSensor.Sensor || !unitSensor.UnitTypeInput) {
                      console.warn(
                        '❌ Skipping sensor due to missing fields:',
                        {
                          unitSensorId: unitSensor.UnitSensorId,
                          hasSensor: !!unitSensor.Sensor,
                          hasUnitTypeInput: !!unitSensor.UnitTypeInput,
                        },
                      );
                      return null;
                    }

                    try {
                      const fetchedData = await getData({
                        from: formatAndEncodeTimestampForAPI(new Date(from)),
                        to: formatAndEncodeTimestampForAPI(new Date(to)),
                        unitId: unit.UnitId,
                        datatype: 'temperature',
                        unitTypeInputId:
                          unitSensor.UnitTypeInput.UnitTypeInputId,
                        aggregateType: 'hour',
                      }).unwrap(); // 👈 unwrap the promise returned by the trigger

                      if (!fetchedData || fetchedData.length === 0) {
                        console.warn(
                          '⚠️ No data returned for',
                          unit.UnitId,
                          unitSensor.SensorId,
                        );
                        return null;
                      }

                      return {
                        name: `${unitSensor?.CustomName || 'Sensor'} (ID: ${unit.UnitId})`,
                        base: fetchedData[0]?.Base,
                        data: fetchedData.map((item) => [
                          toUTC(item.Timestamp),
                          parseFloat(item.Value.toFixed(2)),
                          item.Base,
                        ]),
                        type: 'spline',
                        showInNavigator: true,
                      };
                    } catch (e) {
                      console.error(
                        `❌ Failed fetching data for Unit ${unit.UnitId}, Sensor ${unitSensor.SensorId}`,
                        e,
                      );
                      return null;
                    }
                  }),
                );

                const filteredSeries = sensorSeries.filter(Boolean);
                seriesPerUnit[unit.UnitId] = filteredSeries;
              }),
            );

            const unitsWithData = units.map((unit) => ({
              ...unit,
              Data: seriesPerUnit[unit.UnitId] || [],
            }));

            setAssociationContent((prev) => ({
              ...prev,
              Units: unitsWithData,
            }));
          } else {
            // No DataFrom/DataTo — fallback to setting units only
            setAssociationContent((prev) => ({
              ...prev,
              Units: units,
            }));
          }
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Units: [],
          }));
        }

        if (Sensors.length > 0) {
          const sensors = await Promise.all(
            Sensors.map((id) => getSensorsById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: sensors,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: [],
          }));
        }

        if (Buildings.length > 0) {
          const buildings = await Promise.all(
            Buildings.map((id) => getBuildingById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: buildings,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: [],
          }));
        }

        if (JobSubscriptions.length > 0) {
          const subscriptions = await Promise.all(
            JobSubscriptions.map((id) => getSubscriptionById({ id }).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: subscriptions,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: [],
          }));
        }

        if (Gateways.length > 0) {
          const gateways = await Promise.all(
            Gateways.map((id) => getGatewayById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: gateways,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: [],
          }));
        }
      } catch (error) {
        console.error('Error fetching associations:', error);
      }
    };

    const hasAnyAssociations =
      Units?.length > 0 ||
      Sensors?.length > 0 ||
      Buildings?.length > 0 ||
      JobSubscriptions?.length > 0 ||
      Gateways?.length > 0;

    if (hasAnyAssociations) {
      fetchAssociations();
    }
  }, [
    alarm,
    getBuildingById,
    getData,
    getGatewayById,
    getSensorsById,
    getSubscriptionById,
    getUnitById,
  ]);

  if (!alarm) return null;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      backgroundColor="background.paper"
    >
      <DialogTitle gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
        {alarm?.Title}
      </DialogTitle>
      <DialogContent dividers>
        <Alert
          variant="standard"
          severity={getAlertSeverity(alarm.Severity)}
          icon={SEVERITY_BUTTON_CONFIG[alarm.Severity]?.icon || null}
          sx={{
            borderLeft: '6px solid',
            borderColor: SEVERITY_COLORS[alarm.Severity] || 'divider',
            mb: 2,
          }}
        >
          <AlertTitle gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
            {alarm?.AlarmType}
            <ArrowForwardRoundedIcon fontSize="small" /> {alarm?.AlarmSubtype}
          </AlertTitle>
          <Typography variant="caption" color="text.secondary">
            {alarm.Message ? `Message: ${alarm.Message}` : ''}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {alarm.Details ? `Details: ${alarm.Details}` : ''}
          </Typography>
        </Alert>
        <Stack display="flex" direction="row" gap={2} flexWrap="wrap" mb={2}>
          {/* Buildings */}
          {associationContent &&
            associationContent.Buildings?.map((building) => (
              <Card
                key={building.BuildingId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack
                    direction={'row'}
                    spacing={1}
                    alignItems="center"
                    mb={1}
                  >
                    <HomeWorkRoundedIcon />
                    <Typography variant="subtitle1">
                      {building.Name || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Building ID: {building.BuildingId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address:{' '}
                      {building.Location.Address || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Job Subscriptions */}
          {associationContent &&
            associationContent.JobSubscriptions?.map((job) => (
              <Tooltip
                key={`job-tooltip-${job.JobSubscriptionId}`}
                title={job.JobType || 'No description available'}
              >
                <Card
                  key={job.BuildingId}
                  variant="outlined"
                  flex={1}
                  sx={{ minWidth: 350 }}
                >
                  <CardContent>
                    <Stack direction={'row'} spacing={1} mb={1}>
                      <ReceiptLongRoundedIcon />
                      <Typography variant="subtitle1">
                        {job.JobType || 'Unknown Sensor Type'}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        JobSubscription ID: {job.JobSubscriptionId}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Name: {job.DisplayName}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Tooltip>
            ))}

          {/* Gateways */}
          {associationContent &&
            associationContent.Gateways?.map((gateway) => (
              <Card
                key={gateway.GatewayId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack direction={'row'} spacing={1} mb={1}>
                    <div
                      style={{
                        width: '25px',
                        height: '25px',
                        backgroundColor: '#323232',
                        borderRadius: '40%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          border: '1.5px solid #fff',
                          // border: '1.5px solid #AEF359',
                          borderRadius: '50%',
                        }}
                      ></div>
                    </div>
                    <Typography variant="subtitle1">
                      {gateway.Name || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Gateway ID: {gateway.GatewayId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Online: {gateway.Online ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Activity: {formatDanishDate(gateway.LastActivity)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Application Firmware:{' '}
                      {gateway.CurrentApplicationFirmware}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Sensors */}
          {associationContent &&
            associationContent.Sensors?.map((sensor) => (
              <Card
                key={sensor.SensorId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack
                    direction={'row'}
                    spacing={1}
                    alignItems="center"
                    mb={1}
                  >
                    <SpeedRoundedIcon />
                    <Typography variant="subtitle1">
                      {sensor.SensorType || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sensor ID: {sensor.SensorId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Last Recorded:{' '}
                      {formatDanishDate(sensor.DataLastRecorded) ||
                        'Unknown Sensor Type'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Paired Gateway ID: {sensor.PairedGatewayId || 'N/A'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Interval: {sensor.DataInterval}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Application Firmware:{' '}
                      {sensor.CurrentApplicationFirmware}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Units */}
          {associationContent &&
            associationContent.Units?.map((unit) => (
              <Card
                key={unit.UnitId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack direction={'row'} spacing={1} mb={1}>
                    <RouterRoundedIcon />
                    <Typography variant="subtitle1">
                      {unit.UnitType.Name || 'Unknown'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Unit ID: {unit.UnitId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name: {unit.Name}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
        </Stack>
        <UnitAlarmChart
          alarm={alarm}
          unitData={associationContent?.Units?.[0]?.Data || []}
          isLoading={isLoadingData || isFetchingData}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAlarmDialog;
